// src/pages/OrderNow.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OrderNow.css';

export default function OrderNow() {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState('boston');
  const [dishes, setDishes] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Auto-open cart if there are items
  useEffect(() => {
    if (cart.length > 0) {
      setShowCart(true);
    }
  }, [cart.length]);

  const CITIES = [
    { id: 'toronto', name: 'Toronto' },
    { id: 'boston', name: 'Boston' },
    { id: 'new-york', name: 'New York' },
    { id: 'chicago', name: 'Chicago' },
    { id: 'new-orleans', name: 'New Orleans' },
    { id: 'miami', name: 'Miami' },
    { id: 'austin', name: 'Austin' }
  ];

  useEffect(() => {
    loadDishesForCity(selectedCity);
  }, [selectedCity]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('checkout_cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        console.log('🛒 Loaded cart from localStorage:', parsedCart);
        setCart(parsedCart);
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  }, []);

  const loadDishesForCity = async (city) => {
    setLoading(true);
    try {
      // Load dishes
      const dishesResponse = await fetch(`/api/dishes?city=${city}&limit=50`);
      const dishesData = await dishesResponse.json();
      
      // Load chefs to get chef names
      const chefsResponse = await fetch(`/api/chefs?city=${city}`);
      const chefsData = await chefsResponse.json();

      console.log('🍽️ Dishes API response:', dishesData);
      console.log('👨‍🍳 Chefs API response:', chefsData);
      console.log('📊 Dishes count:', dishesData.count || dishesData.dishes?.length || 0);
      console.log('📊 Chefs count:', chefsData.results?.length || 0);

      // API returns 'dishes' not 'results'
      const loadedDishes = dishesData.dishes || dishesData.results || [];
      const loadedChefs = chefsData.results || [];
      
      console.log('✅ Setting dishes:', loadedDishes.length, 'dishes');
      console.log('✅ Setting chefs:', loadedChefs.length, 'chefs');

      setDishes(loadedDishes);
      setChefs(loadedChefs);
    } catch (error) {
      console.error('❌ Error loading dishes:', error);
      setDishes([]);
      setChefs([]);
    } finally {
      setLoading(false);
    }
  };

  const getChefName = (chefId) => {
    const chef = chefs.find(c => c.id === chefId);
    return chef?.name || 'Unknown Chef';
  };

  const addToCart = (dish) => {
    const existingItem = cart.find(item => item.dish.id === dish.id);
    let updatedCart;
    
    if (existingItem) {
      updatedCart = cart.map(item => 
        item.dish.id === dish.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      updatedCart = [...cart, { dish, quantity: 1 }];
    }
    
    setCart(updatedCart);
    localStorage.setItem('checkout_cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
    console.log('Added to cart:', dish.name);
  };

  const removeFromCart = (dishId) => {
    const updatedCart = cart.filter(item => item.dish.id !== dishId);
    setCart(updatedCart);
    localStorage.setItem('checkout_cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const updateQuantity = (dishId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(dishId);
    } else {
      const updatedCart = cart.map(item => 
        item.dish.id === dishId 
          ? { ...item, quantity: newQuantity }
          : item
      );
      setCart(updatedCart);
      localStorage.setItem('checkout_cart', JSON.stringify(updatedCart));
      window.dispatchEvent(new Event('cartUpdated'));
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.dish.price_cents * item.quantity), 0);
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleCheckout = () => {
    console.log('🛒 Proceeding to checkout with cart:', cart);
    // Save cart to localStorage as backup
    localStorage.setItem('checkout_cart', JSON.stringify(cart));
    // Navigate to checkout with cart data
    navigate('/checkout', { state: { cart } });
  };

  return (
    <div className="order-now-page">
      <div className="order-header">
        <h1>Order Delicious Meals</h1>
        <p className="order-subtitle">From local chefs in {CITIES.find(c => c.id === selectedCity)?.name}</p>
      </div>

      {/* City Selector */}
      <div className="city-selector-container">
        <h2>Select Your City:</h2>
        <div className="city-pills">
          {CITIES.map(city => (
            <button
              key={city.id}
              className={`city-pill ${selectedCity === city.id ? 'active' : ''}`}
              onClick={() => setSelectedCity(city.id)}
            >
              {city.name}
            </button>
          ))}
        </div>
      </div>

      {/* Dishes Grid */}
      <div className="dishes-container">
        {loading ? (
          <div className="loading">Loading dishes...</div>
        ) : dishes.length === 0 ? (
          <div className="no-dishes">
            <p>No dishes available in {CITIES.find(c => c.id === selectedCity)?.name} yet.</p>
            <p>Check back soon or try another city!</p>
          </div>
        ) : (
          <div className="dishes-grid">
            {dishes.map(dish => (
              <div key={dish.id} className="dish-card">
                {dish.image_url && (
                  <div className="dish-image">
                    <img src={dish.image_url} alt={dish.name} />
                  </div>
                )}
                <div className="dish-info">
                  <h3 className="dish-name">{dish.name}</h3>
                  <p className="chef-name">by {getChefName(dish.chef_id)}</p>
                  {dish.description && (
                    <p className="dish-description">{dish.description}</p>
                  )}
                  <div className="dish-footer">
                    <span className="dish-price">{formatPrice(dish.price_cents)}</span>
                    <button 
                      className="add-to-cart-btn"
                      onClick={() => addToCart(dish)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Button */}
      {cart.length > 0 && (
        <button 
          className="cart-toggle-btn"
          onClick={() => setShowCart(!showCart)}
        >
          🛒 Cart ({cart.length}) - {formatPrice(getCartTotal())}
        </button>
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <div className="cart-overlay" onClick={() => setShowCart(false)}>
          <div className="cart-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="cart-header">
              <h2>Your Cart</h2>
              <button className="close-cart" onClick={() => setShowCart(false)}>×</button>
            </div>
            <div className="cart-items">
              {cart.map(item => (
                <div key={item.dish.id} className="cart-item">
                  <div className="cart-item-info">
                    <h4>{item.dish.name}</h4>
                    <p className="cart-item-chef">{getChefName(item.dish.chef_id)}</p>
                    <p className="cart-item-price">{formatPrice(item.dish.price_cents)} each</p>
                  </div>
                  <div className="cart-item-controls">
                    <button 
                      className="qty-btn"
                      onClick={() => updateQuantity(item.dish.id, item.quantity - 1)}
                    >
                      −
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button 
                      className="qty-btn"
                      onClick={() => updateQuantity(item.dish.id, item.quantity + 1)}
                    >
                      +
                    </button>
                    <button 
                      className="remove-btn"
                      onClick={() => removeFromCart(item.dish.id)}
                    >
                      🗑️
                    </button>
                  </div>
                  <div className="cart-item-total">
                    {formatPrice(item.dish.price_cents * item.quantity)}
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-footer">
              <div className="cart-total">
                <span>Total:</span>
                <span className="total-amount">{formatPrice(getCartTotal())}</span>
              </div>
              <button className="checkout-btn" onClick={handleCheckout}>
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

